import React from "react";
import {
  Watch, Circle, Mic, Waves, Activity, HeartPulse, Moon, Thermometer, Gauge,
  ShieldCheck, ClipboardList, Search, Sparkles, Clock, Brain,
  MessageSquareHeart, ArrowLeftRight,
} from "lucide-react";

/**
 * From Biometric Signals to Personal Understanding (consolidated)
 * Physiology<->Psychology bridge, a 5-stage flow, bring-your-own-device (Apple Watch + Oura),
 * a research/evidence panel with collapsible sources, a pilot readiness survey, and a privacy panel.
 * Styling matches the rest of /ana (glass, teal/violet, Outfit/Manrope).
 */

const stages = [
  { n: "01", icon: Watch, title: "A signal appears",
    body: "A wearable detects a meaningful change in an available physiological or recovery indicator." },
  { n: "02", icon: Mic, title: "The nurse is invited, never interrupted",
    body: "My Mood Groove may offer an optional check-in through voice or text. The nurse can accept, postpone, dismiss, or disable the invitation." },
  { n: "03", icon: Search, title: "The nurse adds context",
    body: "The nurse identifies what may be contributing, such as workload, grief, conflict, fatigue, sleep disruption, a difficult event, or something outside work.",
    chips: ["Workload", "A difficult event", "Grief", "Conflict", "Fatigue", "Sleep disruption", "Something outside work"] },
  { n: "04", icon: Waves, title: "A relevant response is offered",
    body: "The nurse may choose reflection, decompression, grounding, a soundscape, preparation for a conversation, or connection with an appropriate resource.",
    chips: ["Reflection", "Decompression", "Grounding", "Soundscape", "Preparation for a conversation", "Resource connection"] },
  { n: "05", icon: Clock, title: "Awareness develops over time",
    body: "The nurse may begin to recognize relationships among work, emotion, recovery, sleep, and physiological signals." },
];

const devices = [
  { icon: Watch, name: "Apple Watch", role: "May provide available signals related to heart rate, activity, sleep, mindfulness activity, and other permitted health data." },
  { icon: Circle, name: "Oura Ring", role: "May provide available signals related to sleep, recovery, HRV, activity, temperature trends, and overnight physiological patterns." },
];

const signalChips = [
  { icon: HeartPulse, label: "Heart rate" },
  { icon: Activity, label: "HRV" },
  { icon: Moon, label: "Sleep" },
  { icon: Gauge, label: "Activity" },
  { icon: Waves, label: "Recovery" },
  { icon: Thermometer, label: "Temperature" },
  { icon: Sparkles, label: "Stress-related indicators" },
];

const surveyQuestions = [
  "Do you currently use a smartwatch, fitness tracker, or smart ring?",
  "Which device do you use?",
  "Which signals do you regularly review?",
  "Would you voluntarily connect selected signals to a private nurse awareness experience?",
  "What privacy protections would you require before connecting your device?",
];

const privacyPoints = [
  "Wearable connection is always optional.",
  "The nurse chooses whether to connect a device and which available signals may be used.",
  "Individual biometric records, private conversations, summaries, excerpts, identities, and personal emotional profiles are not shared with employers or ANA.",
  "Biometric information is not used for discipline, productivity scoring, credentialing, fitness-for-duty assessment, insurance decisions, or employment decisions.",
  "Any organizational reporting must be consent-based, deidentified, aggregated, and subject to appropriate minimum population thresholds.",
];

const BiometricBridge = () => {
  return (
    <div data-testid="biometric-bridge">
      {/* Physiology <-> Awareness bridge */}
      <div className="reveal grid md:grid-cols-[1fr_auto_1fr] gap-4 items-stretch mb-6">
        <div className="glass rounded-2xl p-6 sm:p-7">
          <div className="flex items-center gap-2 mb-3">
            <Brain size={16} className="text-sky-300" />
            <span className="text-[11px] uppercase tracking-[0.22em] text-sky-300">Physiology</span>
          </div>
          <p className="font-heading text-white text-lg leading-snug mb-1">What the body may be signaling.</p>
          <p className="text-sm text-zinc-400 leading-relaxed">Heart rate, HRV, sleep, activity, recovery, temperature, and other available wearable indicators.</p>
        </div>
        <div className="flex md:flex-col items-center justify-center gap-2 px-2">
          <span className="w-11 h-11 rounded-full bg-gradient-to-br from-teal-400/80 to-violet-500/70 flex items-center justify-center shadow-[0_0_24px_rgba(45,212,191,0.35)]">
            <ArrowLeftRight size={18} className="text-white" />
          </span>
          <span className="text-[10px] uppercase tracking-[0.2em] text-teal-300 text-center leading-tight">The<br className="hidden md:block" /> bridge</span>
        </div>
        <div className="glass rounded-2xl p-6 sm:p-7">
          <div className="flex items-center gap-2 mb-3">
            <MessageSquareHeart size={16} className="text-violet-300" />
            <span className="text-[11px] uppercase tracking-[0.22em] text-violet-300">Awareness</span>
          </div>
          <p className="font-heading text-white text-lg leading-snug mb-1">What the nurse understands.</p>
          <p className="text-sm text-zinc-400 leading-relaxed">The experience, pressure, relationship, emotion, event, workload, or recovery need that may surround the signal.</p>
        </div>
      </div>
      <p className="reveal text-center max-w-2xl mx-auto text-zinc-300 leading-relaxed mb-14">
        The wearable notices a change. <span className="text-teal-200">My Mood Groove helps the nurse explore the personal context surrounding it</span>, without diagnosing it.
      </p>

      {/* 5-stage flow */}
      <div className="reveal grid gap-4 md:grid-cols-5 mb-8">
        {stages.map((s) => (
          <div key={s.n} className="glass rounded-2xl p-5 flex flex-col" data-testid={`journey-stage-${s.n}`}>
            <div className="flex items-center justify-between mb-3">
              <span className="font-heading text-2xl text-white/25">{s.n}</span>
              <span className="inline-flex w-9 h-9 rounded-xl bg-white/[0.05] border border-teal-300/25 items-center justify-center">
                <s.icon size={16} className="text-teal-300" />
              </span>
            </div>
            <h4 className="font-heading text-white text-[15px] leading-tight mb-1.5">{s.title}</h4>
            <p className="text-[12px] text-zinc-400 leading-relaxed">{s.body}</p>
            {s.chips && (
              <div className="mt-3 flex flex-wrap gap-1.5">
                {s.chips.map((c) => (
                  <span key={c} className="text-[10px] px-2 py-0.5 rounded-full bg-white/[0.05] border border-white/10 text-zinc-300">{c}</span>
                ))}
              </div>
            )}
          </div>
        ))}
      </div>

      <p className="reveal text-center max-w-2xl mx-auto text-zinc-300 leading-relaxed mb-14">
        Wearables reveal physiological change. <span className="text-teal-200">My Mood Groove helps the nurse understand the story surrounding it</span> and choose what may come next.
      </p>

      {/* Optional wearable connection */}
      <div className="reveal mb-10">
        <div className="text-[11px] uppercase tracking-[0.22em] text-teal-300 mb-4 text-center">Optional Wearable Connection</div>
        <div className="grid sm:grid-cols-2 gap-4 max-w-3xl mx-auto">
          {devices.map(({ icon: Icon, name, role }) => (
            <div key={name} className="glass rounded-2xl p-6 flex items-start gap-4">
              <span className="shrink-0 w-12 h-12 rounded-2xl bg-white/[0.05] border border-white/10 flex items-center justify-center">
                <Icon size={22} className="text-white" />
              </span>
              <div>
                <div className="font-heading text-white text-[15px]">{name}</div>
                <p className="text-[13px] text-zinc-400 leading-relaxed mt-1">{role}</p>
              </div>
            </div>
          ))}
        </div>
        <p className="mt-4 text-center text-[12px] text-zinc-500 italic max-w-2xl mx-auto">
          Device availability, signal access, and integration capabilities would be confirmed during technical discovery.
        </p>
      </div>

      {/* Pilot readiness survey */}
      <div className="grid lg:grid-cols-1 gap-4">
        <div className="reveal glass rounded-3xl p-7 max-w-3xl mx-auto w-full" data-testid="journey-survey">
          <div className="flex items-center gap-2 mb-3">
            <ClipboardList size={16} className="text-teal-300" />
            <span className="text-[11px] uppercase tracking-[0.22em] text-teal-300">Pilot Question: Wearable Readiness</span>
          </div>
          <p className="text-[13px] text-zinc-400 leading-relaxed mb-4">
            A pilot could establish a voluntary baseline for wearable use, signal preferences, and privacy
            expectations among participating nurses. Detailed adoption evidence and integration requirements
            would be documented in a jointly developed pilot appendix.
          </p>
          <ol className="space-y-2.5">
            {surveyQuestions.map((q, i) => (
              <li key={i} className="flex gap-3 text-[13px] text-zinc-300 leading-relaxed">
                <span className="font-heading text-teal-300 text-[12px] shrink-0">{String(i + 1).padStart(2, "0")}</span>
                <span>{q}</span>
              </li>
            ))}
          </ol>
          <div className="mt-3 flex flex-wrap gap-1.5">
            {signalChips.map(({ icon: Icon, label }) => (
              <span key={label} className="inline-flex items-center gap-1 text-[10px] px-2 py-0.5 rounded-full bg-white/[0.05] border border-white/10 text-zinc-300">
                <Icon size={10} className="text-teal-300" /> {label}
              </span>
            ))}
          </div>
        </div>
      </div>

      {/* Privacy panel */}
      <div className="reveal glass-strong rounded-3xl p-7 sm:p-9 mt-4 border border-teal-300/20" data-testid="journey-privacy">
        <div className="flex items-center gap-2 mb-4">
          <ShieldCheck size={18} className="text-teal-300" />
          <h3 className="font-heading text-white text-xl leading-tight">Privacy must come first</h3>
        </div>
        <div className="grid sm:grid-cols-2 gap-x-8 gap-y-3">
          {privacyPoints.map((p) => (
            <div key={p} className="flex items-start gap-2.5">
              <span className="mt-2 w-1.5 h-1.5 rounded-full bg-teal-300 shrink-0" />
              <p className="text-[13px] text-zinc-300 leading-relaxed">{p}</p>
            </div>
          ))}
        </div>
        <p className="mt-5 text-[12px] text-zinc-500 italic leading-relaxed">
          The pilot architecture would be designed so that individual biometric records, transcripts,
          identities, and personal emotional profiles remain private. Final technical safeguards would be
          established through pilot agreements, legal review, privacy review, technical validation, and
          independent governance.
        </p>
      </div>
    </div>
  );
};

export default BiometricBridge;
